import { useState, useEffect } from "react";
import { SUPABASE_URL, authH } from "../config";

// ─── Business name & VAT, used when agreements are filled in ─────────────────
// Kept in app_settings under key 'agreements' = { business_name, copy_email, vat: { registered, rate, number } }
// copy_email: the business's own inbox, sent a copy of every signed agreement (api/esign.js).
// (see src/shared/agreementFields.js for how VAT is worked out).
export function BusinessSettings({ token, settings, showToast, onSaved }) {
  const saved = settings?.vat;
  const initial = () => ({
    name: settings?.business_name || "",
    copyEmail: settings?.copy_email || "",
    registered: saved?.registered === true ? "yes" : saved?.registered === false ? "no" : "",
    rate: String(saved?.rate ?? 20), number: saved?.number || "",
  });
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(initial()); }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function save(e) {
    e.preventDefault();
    const rate = Number(form.rate);
    if (form.registered === "yes" && !(rate >= 0 && rate <= 100)) { showToast("❌ Enter the VAT rate as a percentage, e.g. 20"); return; }
    const copyEmail = form.copyEmail.trim();
    if (copyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(copyEmail)) { showToast("❌ Enter a valid email address for the business copy, or leave it blank"); return; }
    setBusy(true);
    const value = {
      ...(settings || {}),
      business_name: form.name.trim(),
      copy_email: copyEmail || undefined,
      vat: form.registered === "yes" ? { registered: true, rate, number: form.number.trim() } : form.registered === "no" ? { registered: false } : undefined,
    };
    const r = await fetch(`${SUPABASE_URL}/rest/v1/app_settings`, {
      method: "POST", headers: { ...authH(token), Prefer: "resolution=merge-duplicates,return=minimal" },
      body: JSON.stringify({ key: "agreements", value, updated_at: new Date().toISOString() }),
    }).catch(() => null);
    setBusy(false);
    if (!r?.ok) { showToast("❌ Couldn't save — please try again"); return; }
    showToast("✅ Saved");
    onSaved(value);
  }

  return (
    <div className="card">
      <div className="ch"><div className="ct">🧾 Business, VAT & signed copies</div></div>
      <div className="cb">
        <p className="tsub tsm" style={{ marginBottom: 12 }}>
          Used when agreements are filled in. Prices you've entered on a unit (ex-VAT and inc-VAT rent) are always kept; anything missing is worked out at your rate.
          {!saved && " VAT isn't set yet — until it is, each unit's rents are used exactly as entered."}
        </p>
        <form onSubmit={save}>
          <div className="fg">
            <div className="fgi"><label>Business name on agreements</label>
              <input value={form.name} onChange={set("name")} placeholder="CamStorage" /></div>
            <div className="fgi"><label>Send a copy of every signed agreement to</label>
              <input type="email" value={form.copyEmail} onChange={set("copyEmail")} placeholder="e.g. admin@camstorage.co.uk" />
              <div className="tsub tsm" style={{ marginTop: 4 }}>The business's own inbox. Signed copies still go to the customer, whoever sent it and whoever signed for the business. Leave blank for none.</div></div>
            <div className="fgi"><label>Are you VAT registered?</label>
              <select value={form.registered} onChange={set("registered")}>
                <option value="">— Not set —</option>
                <option value="yes">Yes — charge VAT on rent</option>
                <option value="no">No — no VAT to add</option>
              </select></div>
            {form.registered === "yes" && <>
              <div className="fgi"><label>VAT rate (%)</label>
                <input type="number" min="0" max="100" step="0.5" value={form.rate} onChange={set("rate")} /></div>
              <div className="fgi"><label>VAT number (optional)</label>
                <input value={form.number} onChange={set("number")} placeholder="e.g. GB123456789" /></div>
            </>}
          </div>
          <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </form>
      </div>
    </div>
  );
}
